import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FaBoxes, FaSearch, FaPlus, FaSave, FaTimes, 
  FaEdit, FaToggleOn, FaToggleOff, FaFilter, FaSync,
  FaChevronLeft, FaChevronRight, FaTag, FaDollarSign,
  FaBarcode, FaBuilding, FaPercent, FaEye, FaEyeSlash, FaStar
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { NumericFormat } from 'react-number-format';
import { useToast } from '../components/ToastContext';

function GerenciarProdutos() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();

  // Estados principais
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [modalEdicao, setModalEdicao] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState(null);
  
  // Estados de filtro
  const [buscaNome, setBuscaNome] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  
  // Estados de paginação
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalElementos, setTotalElementos] = useState(0);
  const [itensPorPagina] = useState(10);
  
  // Estado do formulário
  const [formProduto, setFormProduto] = useState({
    nome: '',
    preco: '',
    codigoTributacaoNacional: '',
    codigoTributacaoMunicipal: '',
    aliquotaIss: '',
    descricaoNfse: '',
    prioridade: false,
    ativo: true
  });
  const [errors, setErrors] = useState({});

  // Headers com token
  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  // Formatação de moeda
  const formatarMoeda = (valor) => {
    if (valor === null || valor === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valor);
  };

  // Converter string para número (moeda)
  const parseMoeda = (valorString) => {
    if (!valorString) return 0;
    const valorNumerico = parseFloat(
      valorString
        .replace(/\./g, '')
        .replace(',', '.')
        .replace(/[^\d,.-]/g, '')
    );
    return isNaN(valorNumerico) ? 0 : valorNumerico;
  };

  // Formatar percentual
  const formatarPercentual = (valor) => {
    if (!valor && valor !== 0) return '';
    return `${valor}%`;
  };

  // Buscar produtos com paginação e filtro por nome
  const buscarProdutos = useCallback(async () => {
    setLoading(true);
    try {
      let produtosData = [];
      let totalItens = 0;
      let url;
      
      if (buscaNome && buscaNome.trim() !== '') {
        url = `${API_BASE_URL}/produtos/nome/${encodeURIComponent(buscaNome)}/pagina?page=${paginaAtual}&size=${itensPorPagina}`;
        const response = await axios.get(url, getAuthHeader());
        
        produtosData = response.data.content || [];
        totalItens = response.data.totalElements || 0;
        
        if (!mostrarInativos) {
          produtosData = produtosData.filter(p => p.ativo === true);
          totalItens = produtosData.length;
        }
        
        setProdutos(produtosData);
        setTotalPaginas(Math.ceil(totalItens / itensPorPagina));
        setTotalElementos(totalItens);
      } else {
        const response = await axios.get(`${API_BASE_URL}/produtos`, getAuthHeader());
        let todosProdutos = response.data || [];
        
        if (!mostrarInativos) {
          todosProdutos = todosProdutos.filter(p => p.ativo === true);
        }
        
        totalItens = todosProdutos.length;
        const start = paginaAtual * itensPorPagina;
        const end = start + itensPorPagina;
        const produtosPaginados = todosProdutos.slice(start, end);
        
        setProdutos(produtosPaginados);
        setTotalPaginas(Math.ceil(totalItens / itensPorPagina));
        setTotalElementos(totalItens);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      addToast('error', 'Erro ao carregar produtos');
      setProdutos([]);
      setTotalPaginas(0);
      setTotalElementos(0);
    } finally {
      setLoading(false);
    }
  }, [buscaNome, paginaAtual, mostrarInativos, itensPorPagina, API_BASE_URL, addToast]);

  useEffect(() => {
    buscarProdutos();
  }, [buscarProdutos]);

  const aplicarFiltro = () => {
    setPaginaAtual(0);
  };

  const limparFiltro = () => {
    setBuscaNome('');
    setMostrarInativos(false);
    setPaginaAtual(0);
  };

  const toggleMostrarInativos = () => {
    setPaginaAtual(0);
    setMostrarInativos(prevState => !prevState);
  };

  // Validar formulário
  const validarFormulario = () => {
    const newErrors = {};
    
    if (!formProduto.nome) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formProduto.nome.length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    }
    
    if (formProduto.preco === undefined || formProduto.preco === null || formProduto.preco === '') {
      newErrors.preco = 'Preço é obrigatório';
    } else {
      const valorNum = typeof formProduto.preco === 'string' ? parseMoeda(formProduto.preco) : formProduto.preco;
      if (valorNum <= 0) {
        newErrors.preco = 'Preço deve ser maior que zero';
      }
    }
    
    if (formProduto.aliquotaIss && formProduto.aliquotaIss !== '') {
      const aliquotaNum = parseFloat(formProduto.aliquotaIss);
      if (isNaN(aliquotaNum) || aliquotaNum < 0 || aliquotaNum > 100) {
        newErrors.aliquotaIss = 'Alíquota deve ser entre 0 e 100';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvar produto (criar ou editar)
  const salvarProduto = async () => {
    if (!validarFormulario()) return;
    
    setLoading(true);
    try {
      const precoNumerico = typeof formProduto.preco === 'string' ? parseMoeda(formProduto.preco) : (formProduto.preco || 0);
      const dadosProduto = {
        nome: formProduto.nome,
        preco: precoNumerico,
        codigoTributacaoNacional: formProduto.codigoTributacaoNacional || null,
        codigoTributacaoMunicipal: formProduto.codigoTributacaoMunicipal || null,
        aliquotaIss: formProduto.aliquotaIss ? parseFloat(formProduto.aliquotaIss) : null,
        descricaoNfse: formProduto.descricaoNfse || null,
        // prioridade: null significa sem prioridade, false também é tratado como sem prioridade no back
        prioridade: formProduto.prioridade === true ? true : false,
        ativo: modalEdicao ? produtoEditando.ativo : true
      };
      
      if (modalEdicao && produtoEditando) {
        await axios.put(
          `${API_BASE_URL}/produtos/${produtoEditando.idProduto}`,
          dadosProduto,
          getAuthHeader()
        );
        addToast('success', 'Produto atualizado com sucesso!');
      } else {
        await axios.post(
          `${API_BASE_URL}/produtos`,
          dadosProduto,
          getAuthHeader()
        );
        addToast('success', 'Produto adicionado com sucesso!');
      }
      
      fecharModal();
      buscarProdutos();
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      addToast('error', `Erro ao salvar produto: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Ativar/Desativar produto
  const toggleAtivarProduto = async (produto) => {
    setLoading(true);
    try {
      const novoStatus = !produto.ativo;
      const actionText = novoStatus ? 'ativado' : 'desativado';
      
      const produtoAtualizado = {
        ...produto,
        ativo: novoStatus
      };
      
      await axios.put(
        `${API_BASE_URL}/produtos/${produto.idProduto}`,
        produtoAtualizado,
        getAuthHeader()
      );
      
      addToast('success', `Produto ${actionText} com sucesso!`);
      buscarProdutos();
    } catch (error) {
      console.error('Erro ao alterar status do produto:', error);
      addToast('error', `Erro ao ${produto.ativo ? 'desativar' : 'ativar'} produto`);
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal para novo produto
  const abrirModalNovo = () => {
    setModalEdicao(false);
    setProdutoEditando(null);
    setFormProduto({
      nome: '',
      preco: '',
      codigoTributacaoNacional: '',
      codigoTributacaoMunicipal: '',
      aliquotaIss: '',
      descricaoNfse: '',
      prioridade: false,
      ativo: true
    });
    setErrors({});
    setModalAberto(true);
  };

  // Abrir modal para editar produto
  const abrirModalEditar = (produto) => {
    setModalEdicao(true);
    setProdutoEditando(produto);
    setFormProduto({
      nome: produto.nome || '',
      preco: produto.preco || 0,
      codigoTributacaoNacional: produto.codigoTributacaoNacional || '',
      codigoTributacaoMunicipal: produto.codigoTributacaoMunicipal || '',
      aliquotaIss: produto.aliquotaIss?.toString() || '',
      descricaoNfse: produto.descricaoNfse || '',
      // Tratar null/undefined como false para exibição no checkbox
      prioridade: produto.prioridade === true,
      ativo: produto.ativo
    });
    setErrors({});
    setModalAberto(true);
  };

  // Fechar modal
  const fecharModal = () => {
    setModalAberto(false);
    setModalEdicao(false);
    setProdutoEditando(null);
    setFormProduto({
      nome: '',
      preco: '',
      codigoTributacaoNacional: '',
      codigoTributacaoMunicipal: '',
      aliquotaIss: '',
      descricaoNfse: '',
      prioridade: false,
      ativo: true
    });
    setErrors({});
  };

  // Mudar página
  const mudarPagina = (novaPagina) => {
    if (novaPagina >= 0 && novaPagina < totalPaginas) {
      setPaginaAtual(novaPagina);
    }
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <div style={styles.titleContainer}>
            <FaBoxes style={styles.titleIcon} />
            <h1 style={styles.title}>CADASTRO DE PRODUTOS</h1>
          </div>
        </div>

        <div style={styles.formContainer}>
          {/* Filtros */}
          <div style={styles.filtrosContainer}>
            <div style={styles.filtrosWrapper}>
              <div style={styles.filtroGroupBusca}>
                <label style={styles.filtroLabel}>
                  <FaSearch style={styles.filtroIcon} />
                  Buscar por nome
                </label>
                <input
                  type="text"
                  value={buscaNome}
                  onChange={(e) => setBuscaNome(e.target.value)}
                  placeholder="Digite o nome do produto..."
                  style={styles.filtroInputBusca}
                  onKeyPress={(e) => e.key === 'Enter' && aplicarFiltro()}
                />
              </div>
              
              <div style={styles.filtroActions}>
                <button
                  onClick={toggleMostrarInativos}
                  style={{
                    ...styles.filtrarButton,
                    backgroundColor: mostrarInativos ? '#e67e22' : '#95a5a6',
                    minWidth: '160px'
                  }}
                  disabled={loading}
                  title={mostrarInativos ? "Ocultar produtos inativos" : "Mostrar produtos inativos"}
                >
                  {mostrarInativos ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  <span style={{ marginLeft: '6px' }}>
                    {mostrarInativos ? 'OCULTAR INATIVOS' : 'MOSTRAR INATIVOS'}
                  </span>
                </button>
                
                <button
                  onClick={aplicarFiltro}
                  style={styles.filtrarButton}
                  disabled={loading}
                >
                  <FaFilter /> FILTRAR
                </button>
                
                <button
                  onClick={limparFiltro}
                  style={styles.limparButton}
                  disabled={loading}
                >
                  <FaSync /> LIMPAR
                </button>
                
                <button
                  onClick={abrirModalNovo}
                  style={styles.addButtonInline}
                  disabled={loading}
                >
                  <FaPlus /> ADICIONAR
                </button>
              </div>
            </div>
          </div>

          {/* Tabela de Produtos */}
          <div style={styles.tableContainer}>
            {loading && produtos.length === 0 ? (
              <div style={styles.loading}>Carregando produtos...</div>
            ) : produtos.length > 0 ? (
              <>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>ID</th>
                      <th style={styles.th}>Nome</th>
                      <th style={styles.th}>Preço</th>
                      <th style={styles.th}>Cód. Trib. Nacional</th>
                      <th style={styles.th}>Cód. Trib. Municipal</th>
                      <th style={styles.th}>Aliq. ISS</th>
                      <th style={styles.th}>Descrição NFSe</th>
                      <th style={styles.th}>Prioridade</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtos.map((produto) => (
                      <tr 
                        key={produto.idProduto} 
                        style={{
                          ...styles.tr,
                          backgroundColor: !produto.ativo ? '#fff5f5' : 'white',
                          opacity: !produto.ativo ? 0.7 : 1
                        }}
                      >
                        <td style={styles.tdId}>{produto.idProduto}</td>
                        <td style={styles.tdNome}>
                          <strong style={{ 
                            textDecoration: !produto.ativo ? 'line-through' : 'none',
                            color: !produto.ativo ? '#999' : '#333'
                          }}>
                            {produto.nome}
                          </strong>
                          {!produto.ativo && (
                            <span style={styles.inativoBadge}>Inativo</span>
                          )}
                        </td>
                        <td style={styles.tdPreco}>
                          <span style={{
                            ...styles.precoDestaque,
                            color: !produto.ativo ? '#999' : '#e67e22'
                          }}>
                            {formatarMoeda(produto.preco)}
                          </span>
                        </td>
                        <td style={styles.tdCodigo}>
                          {produto.codigoTributacaoNacional || '-'}
                        </td>
                        <td style={styles.tdCodigo}>
                          {produto.codigoTributacaoMunicipal || '-'}
                        </td>
                        <td style={styles.tdAliquota}>
                          <span style={{
                            ...styles.aliquotaBadge,
                            backgroundColor: '#e9ecef',
                            color: !produto.ativo ? '#999' : '#2c5a7a'
                          }}>
                            {formatarPercentual(produto.aliquotaIss) || '-'}
                          </span>
                        </td>
                        <td style={styles.tdDescricao}>
                          {produto.descricaoNfse ? (
                            <span title={produto.descricaoNfse} style={{
                              color: !produto.ativo ? '#999' : '#6c757d'
                            }}>
                              {produto.descricaoNfse.length > 30 
                                ? `${produto.descricaoNfse.substring(0, 30)}...` 
                                : produto.descricaoNfse}
                            </span>
                          ) : '-'}
                        </td>
                        {/* Coluna Prioridade */}
                        <td style={styles.tdPrioridade}>
                          {produto.prioridade === true ? (
                            <span style={styles.prioridadeBadgeAtiva} title="Produto prioritário na ordem de serviço">
                              <FaStar size={11} style={{ marginRight: '4px' }} />
                              Sim
                            </span>
                          ) : (
                            <span style={styles.prioridadeBadgeInativa}>
                              Não
                            </span>
                          )}
                        </td>
                        <td style={styles.tdStatus}>
                          <span style={{
                            ...styles.statusBadge,
                            backgroundColor: produto.ativo ? '#27ae60' : '#e74c3c',
                            color: 'white'
                          }}>
                            {produto.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td style={styles.tdAcoes}>
                          <button
                            onClick={() => abrirModalEditar(produto)}
                            style={styles.editButton}
                            disabled={loading}
                            title="Editar"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => toggleAtivarProduto(produto)}
                            style={{
                              ...styles.toggleButton,
                              backgroundColor: produto.ativo ? '#e74c3c' : '#27ae60'
                            }}
                            disabled={loading}
                            title={produto.ativo ? "Desativar produto" : "Ativar produto"}
                          >
                            {produto.ativo ? <FaToggleOn /> : <FaToggleOff />}
                            <span style={{ marginLeft: '4px' }}>
                              {produto.ativo ? 'Desativar' : 'Ativar'}
                            </span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Paginação */}
                {totalPaginas > 1 && (
                  <div style={styles.pagination}>
                    <button
                      onClick={() => mudarPagina(paginaAtual - 1)}
                      disabled={paginaAtual === 0 || loading}
                      style={styles.paginationButton}
                    >
                      <FaChevronLeft />
                    </button>
                    
                    <span style={styles.paginationInfo}>
                      Página {paginaAtual + 1} de {totalPaginas} 
                      {totalElementos > 0 && ` (${totalElementos} produtos)`}
                    </span>
                    
                    <button
                      onClick={() => mudarPagina(paginaAtual + 1)}
                      disabled={paginaAtual === totalPaginas - 1 || loading}
                      style={styles.paginationButton}
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={styles.noResults}>
                <FaBoxes style={styles.noResultsIcon} />
                <p>Nenhum produto encontrado</p>
                <small>Clique em "ADICIONAR" para começar</small>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Adicionar/Editar Produto */}
        {modalAberto && (
          <div style={styles.modalOverlay} onClick={fecharModal}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>
                  <FaBoxes style={styles.modalTitleIcon} />
                  {modalEdicao ? 'Editar Produto' : 'Adicionar Produto'}
                </h2>
                <button onClick={fecharModal} style={styles.modalClose}>
                  <FaTimes />
                </button>
              </div>
              
              <div style={styles.modalContent}>
                <div style={styles.modalForm}>
                  <div style={styles.inputGroup}>
                    <label style={styles.labelRequired}>Nome *</label>
                    <input
                      type="text"
                      value={formProduto.nome}
                      onChange={(e) => setFormProduto({...formProduto, nome: e.target.value})}
                      placeholder="Digite o nome do produto"
                      style={styles.input}
                      autoFocus
                    />
                    {errors.nome && <span style={styles.errorText}>{errors.nome}</span>}
                  </div>
                  
                  <div style={styles.inputGroup}>
                    <label style={styles.labelRequired}>Preço *</label>
                    <div style={styles.valorInputContainer}>
                      <span style={styles.valorSimbolo}>R$</span>
                      <NumericFormat
                        value={formProduto.preco}
                        onValueChange={(values) => {
                          setFormProduto({ ...formProduto, preco: values.floatValue || 0 });
                        }}
                        thousandSeparator="."
                        decimalSeparator=","
                        placeholder="R$ 0,00"
                        decimalScale={2}
                        fixedDecimalScale
                        style={styles.valorInput}
                      />
                    </div>
                    {errors.preco && <span style={styles.errorText}>{errors.preco}</span>}
                  </div>
                  
                  <div style={styles.rowInputs}>
                    <div style={styles.inputGroupHalf}>
                      <label style={styles.label}>
                        <FaBarcode style={styles.labelIcon} />
                        Cód. Tributação Nacional
                      </label>
                      <input
                        type="text"
                        value={formProduto.codigoTributacaoNacional}
                        onChange={(e) => setFormProduto({...formProduto, codigoTributacaoNacional: e.target.value})}
                        placeholder="Ex: 1234"
                        style={styles.input}
                      />
                    </div>
                    
                    <div style={styles.inputGroupHalf}>
                      <label style={styles.label}>
                        <FaBuilding style={styles.labelIcon} />
                        Cód. Tributação Municipal
                      </label>
                      <input
                        type="text"
                        value={formProduto.codigoTributacaoMunicipal}
                        onChange={(e) => setFormProduto({...formProduto, codigoTributacaoMunicipal: e.target.value})}
                        placeholder="Ex: 5678"
                        style={styles.input}
                      />
                    </div>
                  </div>
                  
                  <div style={styles.rowInputs}>
                    <div style={styles.inputGroupHalf}>
                      <label style={styles.label}>
                        <FaPercent style={styles.labelIcon} />
                        Alíquota ISS (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formProduto.aliquotaIss}
                        onChange={(e) => setFormProduto({...formProduto, aliquotaIss: e.target.value})}
                        placeholder="0,00"
                        style={styles.input}
                      />
                      {errors.aliquotaIss && <span style={styles.errorText}>{errors.aliquotaIss}</span>}
                    </div>
                    
                    <div style={styles.inputGroupHalf}>
                      <label style={styles.label}>
                        <FaTag style={styles.labelIcon} />
                        Descrição NFSe
                      </label>
                      <input
                        type="text"
                        value={formProduto.descricaoNfse}
                        onChange={(e) => setFormProduto({...formProduto, descricaoNfse: e.target.value})}
                        placeholder="Descrição que vai na nota"
                        style={styles.input}
                      />
                    </div>
                  </div>

                  {/* Campo Prioridade */}
                  <div style={styles.prioridadeContainer}>
                    <label style={styles.prioridadeLabel}>
                      <input
                        type="checkbox"
                        checked={formProduto.prioridade === true}
                        onChange={(e) => setFormProduto({...formProduto, prioridade: e.target.checked})}
                        style={styles.prioridadeCheckbox}
                      />
                      <FaStar style={{
                        ...styles.prioridadeStarIcon,
                        color: formProduto.prioridade ? '#f39c12' : '#bdc3c7'
                      }} />
                      <div style={styles.prioridadeTexto}>
                        <span style={styles.prioridadeTitulo}>Produto prioritário</span>
                        <span style={styles.prioridadeDescricao}>
                          Quando marcado, este produto será exibido em destaque (ordem alfabética) 
                          no início da lista na tela de Ordem de Serviço, antes dos demais produtos.
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
                
                <div style={styles.modalFooter}>
                  <button
                    onClick={fecharModal}
                    style={styles.cancelButton}
                  >
                    <FaTimes /> Cancelar
                  </button>
                  <button
                    onClick={salvarProduto}
                    style={styles.saveButton}
                    disabled={loading}
                  >
                    <FaSave /> {loading ? 'Salvando...' : (modalEdicao ? 'Atualizar' : 'Salvar')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd',
    marginBottom: '30px'
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  titleIcon: {
    fontSize: '28px',
    color: '#f39c12',
  },
  title: {
    fontSize: '24px',
    margin: 0,
    color: '#333',
    fontWeight: '600'
  },
  formContainer: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 3px 10px rgba(0,0,0,0.1)'
  },
  filtrosContainer: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '10px',
    border: '1px solid #e9ecef'
  },
  filtrosWrapper: {
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  filtroGroupBusca: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    minWidth: '250px'
  },
  filtroLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  filtroIcon: {
    fontSize: '14px',
    color: '#6c757d'
  },
  filtroInputBusca: {
    padding: '10px 12px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    width: '100%',
    outline: 'none',
    transition: 'all 0.2s'
  },
  filtroActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  filtrarButton: {
    padding: '10px 20px',
    backgroundColor: '#f39c12',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    whiteSpace: 'nowrap'
  },
  limparButton: {
    padding: '10px 20px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    whiteSpace: 'nowrap'
  },
  addButtonInline: {
    padding: '10px 20px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    whiteSpace: 'nowrap'
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white'
  },
  th: {
    backgroundColor: '#f8f9fa',
    padding: '16px 20px',
    textAlign: 'left',
    borderBottom: '2px solid #dee2e6',
    fontWeight: '600',
    color: '#495057',
    fontSize: '15px'
  },
  tr: {
    borderBottom: '1px solid #e9ecef',
    transition: 'background-color 0.2s'
  },
  tdId: {
    padding: '16px 20px',
    fontSize: '14px',
    color: '#6c757d',
    fontFamily: 'monospace'
  },
  tdNome: {
    padding: '16px 20px',
    fontSize: '15px',
    position: 'relative'
  },
  inativoBadge: {
    display: 'inline-block',
    marginLeft: '10px',
    padding: '2px 8px',
    backgroundColor: '#e74c3c',
    color: 'white',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600'
  },
  tdPreco: {
    padding: '16px 20px',
    fontWeight: '600'
  },
  precoDestaque: {
    color: '#e67e22',
    fontSize: '16px',
    fontFamily: 'monospace',
    fontWeight: 'bold'
  },
  tdCodigo: {
    padding: '16px 20px',
    fontSize: '14px',
    color: '#6c757d',
    fontFamily: 'monospace'
  },
  tdAliquota: {
    padding: '16px 20px'
  },
  aliquotaBadge: {
    backgroundColor: '#e9ecef',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#2c5a7a',
    display: 'inline-block'
  },
  tdDescricao: {
    padding: '16px 20px',
    fontSize: '13px',
    color: '#6c757d',
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  tdPrioridade: {
    padding: '16px 20px'
  },
  prioridadeBadgeAtiva: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffc107'
  },
  prioridadeBadgeInativa: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    backgroundColor: '#f8f9fa',
    color: '#adb5bd',
    border: '1px solid #dee2e6'
  },
  tdStatus: {
    padding: '16px 20px'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  tdAcoes: {
    padding: '16px 20px',
    whiteSpace: 'nowrap'
  },
  editButton: {
    padding: '8px 14px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    marginRight: '12px',
    transition: 'background-color 0.2s',
    fontSize: '14px'
  },
  toggleButton: {
    padding: '8px 14px',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    padding: '20px',
    borderTop: '1px solid #e9ecef',
    backgroundColor: '#f8f9fa'
  },
  paginationButton: {
    padding: '10px 16px',
    backgroundColor: '#f39c12',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  },
  paginationInfo: {
    fontSize: '15px',
    color: '#6c757d'
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#6c757d',
    fontSize: '16px'
  },
  noResults: {
    textAlign: 'center',
    padding: '60px',
    color: '#6c757d'
  },
  noResultsIcon: {
    fontSize: '48px',
    color: '#bdc3c7',
    marginBottom: '15px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e9ecef'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    color: '#2c3e50',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  modalTitleIcon: {
    fontSize: '22px',
    color: '#f39c12'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6c757d',
    padding: '5px',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.2s'
  },
  modalContent: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  inputGroupHalf: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1
  },
  rowInputs: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap'
  },
  labelRequired: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2c3e50',
    position: 'relative'
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#495057',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  labelIcon: {
    fontSize: '12px',
    color: '#6c757d'
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'all 0.2s',
    outline: 'none'
  },
  valorInputContainer: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    overflow: 'hidden',
    transition: 'all 0.2s'
  },
  valorSimbolo: {
    padding: '10px 12px',
    backgroundColor: '#f8f9fa',
    borderRight: '1px solid #dee2e6',
    fontSize: '14px',
    fontWeight: '600',
    color: '#6c757d'
  },
  valorInput: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    fontSize: '14px',
    outline: 'none'
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px'
  },
  // Estilos do campo prioridade
  prioridadeContainer: {
    padding: '16px',
    backgroundColor: '#fffbf0',
    borderRadius: '8px',
    border: '1px solid #fde68a'
  },
  prioridadeLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    cursor: 'pointer'
  },
  prioridadeCheckbox: {
    width: '18px',
    height: '18px',
    marginTop: '2px',
    cursor: 'pointer',
    accentColor: '#f39c12',
    flexShrink: 0
  },
  prioridadeStarIcon: {
    fontSize: '20px',
    marginTop: '2px',
    flexShrink: 0,
    transition: 'color 0.2s'
  },
  prioridadeTexto: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  prioridadeTitulo: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2c3e50'
  },
  prioridadeDescricao: {
    fontSize: '12px',
    color: '#6c757d',
    lineHeight: '1.4'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #e9ecef'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s'
  }
};

export default GerenciarProdutos;
